import { useEffect, useRef } from "react";

/**
 * Hook that allows to trigger an action when a specific key is help down.
 * @param targetKey The target key from the keyboard
 * @param onActivation The action to trigger when the key is held down.
 * @param repeatIntervalInMs Defines the time it takes to start triggering the callback function
 * @param repeatIntervalDelayInMs Defines the interval time between each call to the callback function
 * @param dependencyArray The dependency array
 */
export const useKeyHold = (
    targetKey: string,
    onActivation: () => void,
    repeatIntervalInMs: number,
    repeatIntervalDelayInMs: number,
    dependencyArray: React.DependencyList
): void => {
    const intervalRef = useRef<number>(null);
    const intervalDelayRef = useRef<number>(null);

    useEffect(() => {
        const downHandler = ({ key }: KeyboardEvent): void => {
            if (key !== targetKey)
                return;

            if (!intervalDelayRef.current)
                onActivation();

            if (repeatIntervalInMs) {
                intervalDelayRef.current ??= window.setTimeout(() => {
                    if (!intervalRef.current)
                        onActivation();

                    intervalRef.current ??= window.setInterval(() => {
                        onActivation();
                    }, repeatIntervalInMs);
                }, repeatIntervalDelayInMs);
            }
        };

        const upHandler = ({ key }: KeyboardEvent): void => {
            if (key !== targetKey)
                return;

            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = undefined;
            }

            if (intervalDelayRef.current) {
                clearTimeout(intervalDelayRef.current);
                intervalDelayRef.current = undefined;
            }
        };

        window.addEventListener("keydown", downHandler);
        window.addEventListener("keyup", upHandler);

        return () => {
            window.removeEventListener("keydown", downHandler);
            window.removeEventListener("keyup", upHandler);

            if (repeatIntervalInMs)
                clearInterval(intervalRef.current);

            if (intervalDelayRef.current)
                clearTimeout(intervalDelayRef.current);
        };
    }, [...dependencyArray, targetKey, repeatIntervalInMs, repeatIntervalDelayInMs]);
};
